import { useEffect, useId, useRef, useState } from "react";
import { signOut, useActiveMemberRole, useSession } from "./auth-client";
import { Dot, I } from "./ui";
import { isPlatformAdminRole } from "../api/platform-roles";

export function UserMenu({ onGo, wsConnected, collapsed }: {
  onGo: (route: string) => void;
  wsConnected: boolean;
  collapsed: boolean;
}) {
  const { data: session, refetch } = useSession();
  const { data: memberRole } = useActiveMemberRole();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onClick = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!session) return null;
  const user = session.user;
  const displayName = user.name?.trim() || user.email.split("@")[0];
  const initials = (user.name?.trim() || user.email).split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
  const isAdmin = isPlatformAdminRole(user.role);
  const orgRole = memberRole?.role;
  const canManageOrg = isAdmin || orgRole === "owner" || orgRole === "admin";
  const subtitle = isAdmin
    ? "Amministratore"
    : orgRole === "owner"
      ? "Titolare"
      : orgRole === "admin"
        ? "Admin azienda"
        : "Membro";

  const onSignOut = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await signOut();
      if (result.error) throw new Error("Logout non riuscito");
      await refetch();
      setOpen(false);
    } catch {
      setError("Impossibile uscire. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`sidebar-foot user-menu${collapsed ? " user-menu-collapsed" : ""}`} ref={ref} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <button type="button" className="user-menu-trigger" ref={triggerRef} aria-label={`Account: ${displayName}`} title={collapsed ? displayName : undefined} aria-expanded={open} aria-controls={open ? panelId : undefined} onClick={() => setOpen(!open)}>
        <span className="avatar">{initials}</span>
        <span className="sidebar-foot-txt">{displayName}<small>{subtitle}</small></span>
        <span className="user-menu-chevron" aria-hidden="true">{I.more}</span>
      </button>
      {open && (
        <div className="user-menu-pop" id={panelId}>
          <div className="user-menu-identity"><strong>{displayName}</strong><span>{user.email}</span></div>
          <div className="user-menu-connection"><Dot kind={wsConnected ? "live" : ""} />{wsConnected ? "Aggiornamenti in tempo reale" : "Riconnessione in corso"}</div>
          <div className="user-menu-sep" />
          {canManageOrg && (
            <>
              {isAdmin && (
                <button type="button" className="user-menu-item" onClick={() => { setOpen(false); onGo("users"); }}>{I.users}Gestisci utenti</button>
              )}
              <button type="button" className="user-menu-item" onClick={() => { setOpen(false); onGo("orgs"); }}>{I.building}Gestisci aziende</button>
              <div className="user-menu-sep" />
            </>
          )}
          <button type="button" className="user-menu-item user-menu-signout" disabled={busy} onClick={() => void onSignOut()}>{busy ? "Uscita in corso…" : "Esci dall’account"}</button>
          {error && <p className="user-menu-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
